// @migration-in-progress
import React, { useState, useEffect } from 'react';
import { Settings, AlertTriangle, Info, Save, X, Plus, Trash2, ExternalLink, Shield, Package } from 'lucide-react';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../catalyst/dialog';
import { Button } from '../catalyst/button';
import { DRILL_TYPES, getDrillRequirements } from '../../config/drillRequirements';
import { useStorageState } from '../../hooks/useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { useDrillRegister } from '../../hooks/domain/useDrillRegister';
import { validatePositiveInteger } from '../../utils/validation';
import logger from 'utils/logger';

/**
 * DrillSettingsModal - Configure drill frequencies and custom drills
 * 
 * Features:
 * - View/edit drill frequencies (within legal limits)
 * - Add custom company drills
 * - Clear compliance warnings
 * - Audit trail of changes
 */
const DrillSettingsModal = ({ isOpen, onClose }) => {
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  const { value: drillSettings, setValue: saveDrillSettings } = useStorageState('drill_settings', {});
  
  // NEW: Get drill register for equipment triggers
  const { drillRegister, updateDrillFrequency, addCustomDrill: addRegisterDrill } = useDrillRegister();
  
  const [localSettings, setLocalSettings] = useState({});
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customDrillForm, setCustomDrillForm] = useState({
    name: '',
    frequency_days: 90,
    description: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize settings - NOW USING DRILL REGISTER
  useEffect(() => {
    if (isOpen && currentVessel && drillRegister) {
      const vesselSettings = drillSettings[currentVessel.id] || {};
      
      // Use drill register as source of truth
      const merged = {};
      drillRegister.forEach(drill => {
        merged[drill.drillType] = {
          ...drill,
          customFrequency: drill.frequencyDays,
          enabled: !drill.archived,
          // Equipment trigger info
          equipmentDescription: drill.equipmentDescription,
          linkedEquipmentIds: drill.linkedEquipmentIds || [],
          linkedEquipmentCategories: drill.linkedEquipmentCategories || [],
          // Auto-included if has equipment dependencies
          autoIncluded: (drill.linkedEquipmentIds && drill.linkedEquipmentIds.length > 0) ||
                       (drill.linkedEquipmentCategories && drill.linkedEquipmentCategories.length > 0)
        };
      });
      
      // Fallback to old requirements if no register
      if (Object.keys(merged).length === 0) {
        const requirements = getDrillRequirements(currentVessel);
        Object.entries(requirements).forEach(([drillType, requirement]) => {
          merged[drillType] = {
            ...requirement,
            customFrequency: vesselSettings.frequencies?.[drillType] || requirement.frequency_days,
            enabled: vesselSettings.enabled?.[drillType] !== undefined 
              ? vesselSettings.enabled[drillType] 
              : true,
            autoIncluded: false
          };
        });
      }
      
      setLocalSettings(merged);
    }
  }, [isOpen, currentVessel, drillSettings, drillRegister]);

  // Get drill display name
  const getDrillDisplayName = (drillType) => {
    if (localSettings[drillType]?.isCustom) {
      return localSettings[drillType].name;
    }
    
    const names = {
      fire: 'Fire Drill',
      abandon_ship: 'Abandon Ship Drill',
      mob: 'Man Overboard (MOB)',
      pollution_response: 'Pollution Response',
      gmdss_dsc: 'GMDSS/DSC Test',
      medical_emergency: 'Medical Emergency',
      flooding: 'Flooding Response',
      steering_nav_fail: 'Steering/Navigation Failure',
      electrical_blackout: 'Electrical Blackout',
      enclosed_space_entry: 'Enclosed Space Entry',
      towing_emergency: 'Towing Emergency',
      collision: 'Collision Response',
      grounding: 'Grounding Response'
    };
    return names[drillType] || drillType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Update frequency
  const updateFrequency = (drillType, frequency) => {
    const drill = localSettings[drillType];
    
    // Validate legal requirements
    if ((drillType === 'fire' || drillType === 'abandon_ship') && frequency > 30) {
      alert('⚠️ Fire and Abandon Ship drills must be conducted at least monthly (30 days) per SI 1999/2722');
      return;
    }
    
    if (drillType === 'mob' && frequency > 90) {
      logger.warn('MOB drills typically interpreted as quarterly maximum');
    }
    
    setLocalSettings({
      ...localSettings,
      [drillType]: {
        ...drill,
        customFrequency: frequency
      }
    });
    setHasChanges(true);
  };

  // Toggle enabled
  const toggleEnabled = (drillType) => {
    const drill = localSettings[drillType];
    
    // Can't disable mandatory drills
    if (drill.compliance_level === 'Required' && !drill.configurable) {
      alert('⚠️ This drill is mandatory and cannot be disabled');
      return;
    }
    
    setLocalSettings({
      ...localSettings,
      [drillType]: {
        ...drill,
        enabled: !drill.enabled
      }
    });
    setHasChanges(true);
  };

  // Add custom drill
  const addCustomDrill = () => {
    if (!customDrillForm.name.trim()) {
      alert('Please enter a drill name');
      return;
    }
    
    const customId = `custom_${Date.now()}`;
    setLocalSettings({
      ...localSettings,
      [customId]: {
        id: customId,
        name: customDrillForm.name,
        customFrequency: customDrillForm.frequency_days,
        frequency_days: customDrillForm.frequency_days,
        description: customDrillForm.description,
        enabled: true,
        isCustom: true,
        compliance_level: 'Custom',
        regulatory_source: 'Company SMS',
        configurable: true,
        note: customDrillForm.description
      }
    });
    
    setCustomDrillForm({ name: '', frequency_days: 90, description: '' });
    setShowAddCustom(false);
    setHasChanges(true);
  };

  // Remove custom drill
  const removeCustomDrill = (drillId) => {
    if (!localSettings[drillId]?.isCustom) return;
    
    const newSettings = { ...localSettings };
    delete newSettings[drillId];
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  // Save settings - NOW SYNCS WITH DRILL REGISTER
  const saveSettings = () => {
    if (!currentVessel) return;
    
    const frequencies = {};
    const enabled = {};
    const customDrills = [];
    
    Object.entries(localSettings).forEach(([drillType, drill]) => {
      if (drill.isCustom || drill.custom) {
        customDrills.push({
          id: drill.id,
          name: drill.name,
          frequency_days: drill.customFrequency,
          description: drill.description || drill.note,
          enabled: drill.enabled
        });
        // Also update in drill register if function available
        if (drill.custom && addRegisterDrill) {
          addRegisterDrill(drill);
        }
      } else {
        frequencies[drillType] = drill.customFrequency;
        enabled[drillType] = drill.enabled;
        
        // Update frequency in drill register if changed
        if (updateDrillFrequency && drill.customFrequency !== drill.frequencyDays) {
          updateDrillFrequency(drillType, drill.customFrequency);
        }
      }
    });
    
    const vesselSettings = {
      frequencies,
      enabled,
      customDrills,
      lastModified: new Date().toISOString(),
      modifiedBy: 'Current User' // Would come from auth
    };
    
    saveDrillSettings({
      ...drillSettings,
      [currentVessel.id]: vesselSettings
    });
    
    setHasChanges(false);
    onClose();
  };

  // Frequency input component
  const FrequencyInput = ({ drillType, frequency, configurable }) => {
    if (!configurable) {
      return (
        <div className="frequency-display locked">
          <span className="frequency-value">{frequency} days</span>
          <span className="frequency-label">MANDATORY</span>
        </div>
      );
    }
    
    return (
      <div className="frequency-input-group">
        <input
          type="number"
          value={frequency}
          onChange={(e) => {
            const result = validatePositiveInteger(
              e.target.value,
              'Drill frequency',
              { min: 7, max: 365, allowEmpty: false }
            );
            
            if (!result.valid) {
              alert(result.error);
              return;
            }
            
            updateFrequency(drillType, result.value);
          }}
          min="7"
          max="365"
          className="frequency-input"
        />
        <span className="frequency-unit">days</span>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} size="4xl">
      <DialogTitle>Drill Configuration</DialogTitle>
      <DialogBody>
        <p className="text-sm text-gray-600 mb-4">Manage drill frequencies and compliance settings</p>
        <div className="settings-content">
        {/* Compliance Warning */}
        <div className="compliance-banner">
          <AlertTriangle size={20} />
          <div>
            <strong>Legal Requirements:</strong> Fire and Abandon Ship drills must be conducted monthly per SI 1999/2722. 
            Other frequencies should align with your SMS and risk assessment.
          </div>
        </div>

        {/* Link to Vessel Settings */}
        <div style={{ 
          marginBottom: '1rem', 
          padding: '0.75rem', 
          background: '#f0f9ff', 
          border: '1px solid #bae6fd',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Info size={16} style={{ color: '#0369a1' }} />
            <span style={{ fontSize: '0.875rem', color: '#0c4a6e' }}>
              Equipment check frequencies and other vessel-wide settings can be configured in the main vessel settings.
            </span>
          </div>
          <button 
            className="btn btn-sm btn-outline"
            onClick={() => {
              onClose();
              // Navigate to vessel settings
              window.location.hash = '#/settings';
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <ExternalLink size={14} />
            Vessel Settings
          </button>
        </div>

        {/* Mandatory Drills */}
        <div className="settings-section">
          <h3>Mandatory Drills</h3>
          <div className="drill-settings-list">
            {Object.entries(localSettings)
              .filter(([_, drill]) => (drill.complianceLevel === 'Required' || drill.compliance_level === 'Required') && !drill.configurable && !drill.isCustom && !drill.custom)
              .map(([drillType, drill]) => (
                <div key={drillType} className="drill-setting-item mandatory">
                  <div className="drill-name">
                    <h4>{getDrillDisplayName(drillType)}</h4>
                    <p>{drill.regulatorySource || drill.regulatory_source}</p>
                    {/* Equipment trigger info */}
                    {drill.autoIncluded && drill.equipmentDescription && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Package size={14} style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Auto-included: {drill.equipmentDescription}
                        </span>
                      </div>
                    )}
                    {!drill.configurable && drill.warning && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Shield size={14} style={{ color: '#dc2626' }} />
                        <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                          {drill.warning}
                        </span>
                      </div>
                    )}
                  </div>
                  <FrequencyInput 
                    drillType={drillType}
                    frequency={drill.customFrequency}
                    configurable={false}
                  />
                </div>
              ))}
          </div>
        </div>

        {/* Required Configurable Drills */}
        <div className="settings-section">
          <h3>Required Drills (Configurable)</h3>
          <div className="drill-settings-list">
            {Object.entries(localSettings)
              .filter(([_, drill]) => (drill.complianceLevel === 'Required' || drill.compliance_level === 'Required') && drill.configurable && !drill.isCustom && !drill.custom)
              .map(([drillType, drill]) => (
                <div key={drillType} className="drill-setting-item required">
                  <div className="drill-name">
                    <h4>{getDrillDisplayName(drillType)}</h4>
                    <p>{drill.regulatorySource || drill.regulatory_source}</p>
                    {/* Equipment trigger info */}
                    {drill.autoIncluded && drill.equipmentDescription && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Package size={14} style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Auto-included: {drill.equipmentDescription}
                        </span>
                      </div>
                    )}
                    {drill.configurable && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Settings size={14} style={{ color: '#059669' }} />
                        <span style={{ fontSize: '0.75rem', color: '#059669' }}>
                          Frequency can be customized
                        </span>
                      </div>
                    )}
                  </div>
                  <FrequencyInput 
                    drillType={drillType}
                    frequency={drill.customFrequency}
                    configurable={true}
                  />
                </div>
              ))}
          </div>
        </div>

        {/* Recommended Drills */}
        <div className="settings-section">
          <h3>Recommended Drills</h3>
          <p className="section-note">Based on your vessel configuration and risk assessment</p>
          <div className="drill-settings-list">
            {Object.entries(localSettings)
              .filter(([_, drill]) => (drill.complianceLevel === 'Recommended' || drill.compliance_level === 'Recommended') && !drill.isCustom && !drill.custom)
              .map(([drillType, drill]) => (
                <div key={drillType} className="drill-setting-item recommended">
                  <div className="drill-enable">
                    <input
                      type="checkbox"
                      checked={drill.enabled}
                      onChange={() => toggleEnabled(drillType)}
                      id={`settings-${drillType}`}
                    />
                  </div>
                  <div className="drill-name">
                    <label htmlFor={`settings-${drillType}`}>
                      <h4>{getDrillDisplayName(drillType)}</h4>
                    </label>
                    <p>{drill.regulatorySource || drill.regulatory_source}</p>
                    {/* Equipment trigger info */}
                    {drill.autoIncluded && drill.equipmentDescription && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Package size={14} style={{ color: '#6b7280' }} />
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Auto-included: {drill.equipmentDescription}
                        </span>
                      </div>
                    )}
                    {!drill.autoIncluded && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                        <Info size={14} style={{ color: '#3b82f6' }} />
                        <span style={{ fontSize: '0.75rem', color: '#3b82f6' }}>
                          Manually added based on risk assessment
                        </span>
                      </div>
                    )}
                  </div>
                  {drill.enabled && (
                    <FrequencyInput 
                      drillType={drillType}
                      frequency={drill.customFrequency}
                      configurable={true}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Custom Drills */}
        <div className="settings-section">
          <div className="section-header">
            <h3>Custom Company Drills</h3>
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => setShowAddCustom(true)}
            >
              <Plus size={16} />
              Add Custom Drill
            </button>
          </div>
          
          {showAddCustom && (
            <div className="custom-drill-form">
              <input
                type="text"
                placeholder="Drill name"
                value={customDrillForm.name}
                onChange={(e) => setCustomDrillForm({ ...customDrillForm, name: e.target.value })}
                className="form-input"
              />
              <input
                type="number"
                placeholder="Frequency (days)"
                value={customDrillForm.frequency_days}
                onChange={(e) => {
                  const result = validatePositiveInteger(
                    e.target.value,
                    'Frequency',
                    { min: 7, max: 365, allowEmpty: false }
                  );
                  
                  if (!result.valid) {
                    // Don't alert for custom drills, just keep the old value
                    return;
                  }
                  
                  setCustomDrillForm({ ...customDrillForm, frequency_days: result.value });
                }}
                className="form-input"
                min="7"
                max="365"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={customDrillForm.description}
                onChange={(e) => setCustomDrillForm({ ...customDrillForm, description: e.target.value })}
                className="form-input"
              />
              <button className="btn btn-sm btn-primary" onClick={addCustomDrill}>
                Add
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowAddCustom(false)}>
                Cancel
              </button>
            </div>
          )}
          
          <div className="drill-settings-list">
            {Object.entries(localSettings)
              .filter(([_, drill]) => drill.isCustom)
              .map(([drillId, drill]) => (
                <div key={drillId} className="drill-setting-item custom">
                  <div className="drill-enable">
                    <input
                      type="checkbox"
                      checked={drill.enabled}
                      onChange={() => toggleEnabled(drillId)}
                      id={`settings-${drillId}`}
                    />
                  </div>
                  <div className="drill-name">
                    <label htmlFor={`settings-${drillId}`}>
                      <h4>{drill.name}</h4>
                    </label>
                    <p>{drill.description || 'Company-specific drill'}</p>
                  </div>
                  {drill.enabled && (
                    <FrequencyInput 
                      drillType={drillId}
                      frequency={drill.customFrequency}
                      configurable={true}
                    />
                  )}
                  <button 
                    className="btn-icon btn-sm"
                    onClick={() => removeCustomDrill(drillId)}
                    title="Remove custom drill"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box">
          <Info size={20} />
          <div>
            <strong>Frequency Guidelines:</strong>
            <ul>
              <li>High Risk Operations: 30-90 days</li>
              <li>Standard Operations: 90-180 days</li>
              <li>Low Risk/Inshore: 180-365 days</li>
            </ul>
            Adjust based on your SMS and risk assessment.
          </div>
        </div>
      </div>
      </DialogBody>

      {/* Modal Footer */}
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={saveSettings}
          disabled={!hasChanges}
        >
          <Save size={16} className="mr-2 inline" />
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DrillSettingsModal;